import { useState } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { margins, paddings } from '@/common/spacing'
import { colors } from '@/common/colors'
import { getCurrentFlow } from '@/common/utils'
import { selectProgress } from '@/store/modules/progress'
import type { RootState } from '@/store/redux-store'
import { Recommendation } from '@/types/types'
import { FlowDetails } from './flow-details'
import { ProgressDetails } from './progress-details'
import {
  mapFlowIdToDisplayName,
  mapFlowIdToIcon,
  mapFlowStageToDisplayName,
} from './utils'

const barZIndex = 999

const toFontAwesomeIcon = (icon: string) => icon.replace(/^fa-/, '')

const FlowDetailsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${colors.navyBlue};
  width: 100%;
  height: 53px;
  padding: ${paddings.s};
`

const IconsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${margins.s};
`

const CurrentTaskTitleWrapper = styled.div`
  position: relative;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-transform: initial;
  line-height: 20px;
  letter-spacing: -0.01em;
  margin-bottom: ${margins.s};
  display: flex;
  align-items: center;
  justify-content: space-between;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -5px;
    width: 90px;
    height: 2px;
    background-color: ${colors.botChatPrimary};
  }
`

const CurrentTaskName = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.01em;
  width: 100%;
  margin-bottom: 5px;
`

const CurrentTaskDescription = styled.div`
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  letter-spacing: -0.01em;
  text-transform: initial;
`

const CurrentTaskWrapper = styled.div`
  width: 90vw;
  background: #feefde;
  border: 1px solid #fa8c16;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  margin-top: ${margins.s};
  color: ${colors.botChatPrimary};
  padding: ${paddings.s};
`

const Wrapper = styled.div`
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: ${barZIndex};
  font-family: Lexend Deca;
  color: #ffffff;
  text-transform: capitalize;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const IconButton = styled.button<{ $isProgressExpanded?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  margin-right: 14px;
  rotate: ${({ $isProgressExpanded }) => ($isProgressExpanded ? '180deg' : '0deg')};
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`

const ExpandedOverlay = styled.button`
  position: absolute;
  height: 100vh;
  width: 100vw;
  background-color: #000000;
  opacity: 0.5;
  top: 0;
  left: 0;
  z-index: ${barZIndex - 1};
  border: none;
  padding: 0;
  cursor: pointer;
`

export interface MobileProgressBarProps {
  onExit?: () => void
}

export const MobileProgressBar = ({ onExit }: MobileProgressBarProps) => {
  const progress = useSelector((state: RootState) => selectProgress(state))
  const [isProgressExpanded, setIsProgressExpanded] = useState(false)
  const [isCurrentTaskExpanded, setIsCurrentTaskExpanded] = useState(true)

  const currentFlow = getCurrentFlow(progress)
  const flow =
    currentFlow && typeof currentFlow === 'string'
      ? progress.flows[currentFlow]
      : undefined
  const currentStage = flow
    ? flow.completed === false
      ? flow.actions.find(action => action.completed === false)
      : flow.actions.find(action => action.completed === true)
    : undefined
  const currentFlowName =
    flow && currentStage && typeof currentFlow === 'string'
      ? mapFlowIdToDisplayName(currentFlow)
      : ''
  const currentFlowIcon =
    flow && currentStage && typeof currentFlow === 'string'
      ? mapFlowIdToIcon(currentFlow)
      : ''
  const currentFlowStage = currentStage ? mapFlowStageToDisplayName(currentStage.id) : ''

  const openTasks = progress.recommendations
    .filter(({ completed }) => !completed)
    .sort((a, b) => a.priority - b.priority)
  const currentTask: Recommendation | undefined =
    openTasks.length > 0 ? openTasks[openTasks.length - 1] : undefined

  if (!currentFlowName) {
    return null
  }

  return (
    <span>
      <Wrapper>
        <FlowDetailsBar>
          <FlowDetails
            currentFlowName={currentFlowName}
            currentFlowStage={currentFlowStage}
            currentFlowIcon={toFontAwesomeIcon(currentFlowIcon)}
          />
          <IconsWrapper>
            {currentTask !== undefined && (
              <IconButton
                type="button"
                onClick={() => setIsCurrentTaskExpanded(value => !value)}
                aria-label="Toggle open tasks"
              >
                <FontAwesomeIcon icon="list-ol" size="xl" color="#fa8c16" />
              </IconButton>
            )}
            <IconButton
              type="button"
              $isProgressExpanded={isProgressExpanded}
              onClick={() => setIsProgressExpanded(value => !value)}
              aria-label="Expand progress"
            >
              <FontAwesomeIcon icon="chevron-down" size="xl" color="#FFFFFF" />
            </IconButton>
          </IconsWrapper>
        </FlowDetailsBar>

        {currentTask !== undefined && !isProgressExpanded && isCurrentTaskExpanded && (
          <CurrentTaskWrapper>
            <CurrentTaskTitleWrapper>
              <span>Open tasks</span>
              <IconButton
                type="button"
                onClick={() => setIsCurrentTaskExpanded(false)}
                aria-label="Close open tasks"
              >
                <FontAwesomeIcon icon="times-circle" size="xl" color="#0E455B" />
              </IconButton>
            </CurrentTaskTitleWrapper>
            <CurrentTaskName>{currentTask.displayName}</CurrentTaskName>
            <CurrentTaskDescription>{currentTask.description}</CurrentTaskDescription>
          </CurrentTaskWrapper>
        )}
        <ProgressDetails
          progress={progress}
          isExpanded={isProgressExpanded}
          onExit={onExit}
        />
      </Wrapper>
      {isProgressExpanded && (
        <ExpandedOverlay
          type="button"
          onClick={() => setIsProgressExpanded(value => !value)}
          aria-label="Collapse progress"
        />
      )}
    </span>
  )
}
